import React from "react"
import { cn } from "@/lib/utils"

type TypographyVariant =
  | "h1"
  | "h2"
  | "h3"
  | "subtitle"
  | "eyebrow"
  | "body"
  | "body-sm";

type TypographyProps = {
  as?: React.ElementType;
  variant?: TypographyVariant;
  className?: string;
  children: React.ReactNode;
  /** Anchor target — needed when a section is labelled by its own heading
      (aria-labelledby) or linked to from elsewhere on the page. */
  id?: string;
  /** Accessible name, for headings whose visible text is a decorative split
      (per-character spans) that assistive tech shouldn't read glyph by glyph. */
  "aria-label"?: string;
};

const variantClassMap: Record<TypographyVariant, string> = {
  h1: "tk-h1",
  h2: "tk-h2",
  h3: "tk-h3",
  subtitle: "tk-subtitle",
  eyebrow: "tk-eyebrow",
  body: "tk-body",
  "body-sm": "tk-body-sm",
};

export function Typography({
  as,
  variant = "body",
  className,
  children,
  id,
  "aria-label": ariaLabel,
}: TypographyProps) {
  const Component = as || defaultElementForVariant(variant);
  const variantClasses = variantClassMap[variant];

  return (
    <Component id={id} aria-label={ariaLabel} className={cn(variantClasses, className)}>
      {children}
    </Component>
  );
}

function defaultElementForVariant(variant: TypographyVariant): keyof JSX.IntrinsicElements {
  switch (variant) {
    case "h1":
      return "h1";
    case "h2":
      return "h2";
    case "h3":
      return "h3";
    case "eyebrow":
      return "p";
    case "subtitle":
      return "p";
    case "body":
    case "body-sm":
    default:
      return "p";
  }
}
